import React, {Suspense} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {BrandType} from '@shared/cci/features/const/environment';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {AdasType} from '@di-shared/api/di/type';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {formatNumberWithCommas} from '@di-shared/utils';
import {ERROR} from '@di-shared/const';
import {ErrorBoundary} from 'react-error-boundary';
import {DrivingInfoAdasProps} from '@di-shared/features/history/driving-info/DrivingInfoAdas/index';

export default function ({vin, selectedDate}: DrivingInfoAdasProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoAdasProps) {
  const {t} = useInferableTranslation();
  const params = {
    vin,
    month: getYYYYMMFormat(selectedDate),
  };
  const {data: adasReport} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ccsReportController.getAdasReport), params],
    queryFn: () => diApi.ccsReportController.getAdasReport(params),
    select: ({data}) => data,
  });

  // No data 화면
  if (!adasReport) {
    return null;
  }

  // Default 화면
  return (
    <>
      {adasReport.isAdasSupport === AdasType.Y && (
        <div className="section_container">
          <h4
            style={{whiteSpace: 'pre-wrap'}}
            className="title_xxsmall"
          >
            {t('T10027')}
          </h4>
          <ul className="device_info_list">
            {adasReport.adasOption.fca && (
              <li className="device_info_item">
                <span className="device_info_icon">
                  <span className="ic ic_svm_front_24px" />
                </span>
                <span className="device_info_key body_large_r">{t('T10028')}</span>
                <span className="device_info_val body_xlarge_b">
                  {t('T10014_$count', {count: formatNumberWithCommas(adasReport.adasData.fcaCnt)})}
                </span>
              </li>
            )}
            {adasReport.adasOption.lka && (
              <li className="device_info_item">
                <span className="device_info_icon">
                  <span className="ic ic_stability_control_24px" />
                </span>
                <span className="device_info_key body_large_r">{t('T10029')}</span>
                <span className="device_info_val body_xlarge_b">
                  {t('T10014_$count', {count: formatNumberWithCommas(adasReport.adasData.lkaCnt)})}
                </span>
              </li>
            )}
            {adasReport.adasOption.scc && (
              <li className="device_info_item">
                <span className="device_info_icon">
                  <span className="ic ic_cruise_control_20px" />
                </span>
                <span className="device_info_key body_large_r">{t('T10030')}</span>
                <span className="device_info_val body_xlarge_b">
                  {t('T10014_$count', {count: formatNumberWithCommas(adasReport.adasData.sccCnt)})}/
                  {adasReport.adasData.sccCnt > 0 && adasReport.adasData.sccMin === 0
                    ? t('T10202')
                    : t('T30104_$minute', {minute: adasReport.adasData.sccMin})}
                </span>
              </li>
            )}
          </ul>
          {import.meta.env.VITE_BRAND === BrandType.KIA && (adasReport.adasData.sccCnt === 0 || adasReport.adasData.sccMin === 0) && (
            <div className="driving_banner type_small">
              <a
                role="button"
                className="driving_banner_box"
                onClick={() => WebviewUtils.openWebBrowser(import.meta.env.VITE_SCC_URL)}
              >
                <span className="driving_banner_txtbox">
                  <strong className="driving_banner_title body_small_b">{t('T10032')}</strong>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
                  />
                </span>
              </a>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container skeleton">
      <h4
        style={{whiteSpace: 'pre-wrap'}}
        className="title_xxsmall"
      >
        {t('T10027')}
      </h4>
      <ul className="device_info_list">
        <li className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_svm_front_24px" />
          </span>
          <span className="device_info_key body_large_r">{t('T10028')}</span>
          <span className="device_info_val body_xlarge_b">{t('T10014_$count', {count: ERROR.DASH})}</span>
        </li>
        <li className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_stability_control_24px" />
          </span>
          <span className="device_info_key body_large_r">{t('T10029')}</span>
          <span className="device_info_val body_xlarge_b">{t('T10014_$count', {count: ERROR.DASH})}</span>
        </li>
        <li className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_cruise_control_20px" />
          </span>
          <span className="device_info_key body_large_r">{t('T10030')}</span>
          <span className="device_info_val body_xlarge_b">{t('T10014_$count', {count: ERROR.DASH})}</span>
        </li>
      </ul>
      <div className="driving_banner type_small">
        <div className="driving_banner_box">
          <span className="driving_banner_txtbox">
            <strong className="driving_banner_title body_small_b">{t('T10032')}</strong>
          </span>
          <span className="driving_banner_imgbox">
            <ImageSelector
              className="driving_banner_img"
              alt="driving_banner_img"
              kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_youtube.png')}
            />
          </span>
        </div>
      </div>
    </div>
  );
}
